import { useRouter } from "next/router";
import ErrorPage from "next/error";
import Container from "@/components/container";
import PostBody from "@/components/post-body";
import MoreStories from "@/components/more-stories";
import Header from "@/components/header";
import PostHeader from "@/components/post-header";
import Layout from "@/components/layout";
import { getAllPostsWithSlug, getPostAndMorePosts } from "@/lib/api";
import PostTitle from "@/components/post-title";
import Head from "next/head";
import { CMS_NAME } from "@/lib/constants";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import Tabs from "@/components/Tabs";
import CounsellingModal from "../CounsellingModalBlog";
import ReviewSection from "@/components/ReviewSection";
import { useFAQToggleHandler, FAQ_TOGGLE_STYLES } from "@/components/FAQToggleHandler";
import readingTime from "reading-time";

export default function Post({ post, morePosts, preview }) {
  const router = useRouter();
  const [userRating, setUserRating] = useState(0);
  const [userReviews, setUserReviews] = useState([]);
  const [reviewContent, setReviewContent] = useState({ content: "" });
  const [hasVoted, setHasVoted] = useState({});
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const isLoggedIn = !!Cookies.get("authToken");
  const userId = isLoggedIn ? Cookies.get("userId") : null;
  const userName = isLoggedIn ? Cookies.get("userName") : null;

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Initialize FAQ toggle handler
  useFAQToggleHandler([post?.id]);

  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  useEffect(() => {
    if (post?.html) {
      post.html = post.html.replace(
        /ghost\.neetsupport\.com\/content\//g,
        "neetsupport.com/content/"
      );
      post.html = post.html.replace(
        /ghost\.neetsupport\.com/g,
        post.canonical_url
      );

      const parser = new DOMParser();
      const doc = parser.parseFromString(post.html, "text/html");

      // Convert all <a> inside .kg-card.kg-button-card.kg-align-center to styled <button>
      doc
        .querySelectorAll(".kg-card.kg-button-card.kg-align-center a")
        .forEach((link) => {
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = link.textContent;
          button.className = "kg-btn kg-btn-accent custom-kg-btn";
          // Set background, color, border directly
          button.style.background = "#ff9245";
          button.style.color = "#fff";
          button.style.border = "1px solid #ff9245";
          button.style.borderRadius = "20px";
          button.style.padding = "4px 20px";
          button.style.fontWeight = "600";
          button.style.fontSize = "1.1rem";
          button.style.cursor = "pointer";
          button.style.transition = "background 0.2s, color 0.2s";
          // Center the button using flexbox on the parent
          const parent = link.parentNode;
          if (parent) {
            parent.style.display = "flex";
            parent.style.justifyContent = "center";
          }
          // Use setAttribute for onclick so it works after innerHTML render
          button.setAttribute(
            "onclick",
            `window.open('${link.href}', '_blank', 'noopener,noreferrer')`
          );
          // Copy inline styles if any (append to our styles)
          if (link.hasAttribute("style")) {
            button.setAttribute(
              "style",
              link.getAttribute("style") +
                ";background:#ff9245;color:#fff;border:2px solid #ff9245;border-radius:20px;padding:4px 20px;font-weight:600;font-size:1.1rem;cursor:pointer;transition:background 0.2s,color 0.2s;"
            );
          }
          link.parentNode.replaceChild(button, link);
        });

      // ...existing code for <a> tag processing...
      doc.querySelectorAll("a").forEach((link) => {
        if (!link.getAttribute("href").startsWith("#")) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");

          const href = link.getAttribute("href");
          const cleanHref = href.split("?")[0];
          link.setAttribute("href", cleanHref);
        }
      });
      post.html = doc.body.innerHTML;
    }

    if (post?.feature_image) {
      post.feature_image = post.feature_image.replace("ghost.", "");
    }

    if (morePosts) {
      morePosts.forEach((morePost) => {
        if (morePost.feature_image) {
          morePost.feature_image = morePost.feature_image.replace("ghost.", "");
        }
      });
    }
  }, [post?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsModalOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleCardClick = () => {
    setTimeout(() => {
      setIsModalOpen(true);
    }, 3000);
  };

  return (
    <Layout preview={preview}>
      <Container>
        <Header />
        {router.isFallback ? (
          <PostTitle>Loading…</PostTitle>
        ) : (
          <>
            <article>
              <Head>
                <title>
                  {post.meta_title || `${post.title} | ${CMS_NAME}`}
                </title>
                <link rel="shortcut icon" href="/favicon.ico" />
                <meta
                  name="description"
                  content={post.meta_description || post.meta_description}
                />
                <meta
                  property="og:title"
                  content={post.title || post.og_title}
                />
                <meta
                  property="og:description"
                  content={post.meta_description || post.og_description}
                />
                <meta
                  property="og:image"
                  content={
                    post.feature_image
                      ? post.feature_image.replace(
                          /^https:\/\/ghost\.neetsupport\.com/,
                          "https://neetsupport.com"
                        )
                      : "https://neetsupport.com/default-image.jpg"
                  }
                />

                <meta property="og:url" content={post.url} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                  name="twitter:title"
                  content={post.twitter_title || post.title}
                />
                <meta
                  name="twitter:description"
                  content={post.twitter_description || post.excerpt}
                />
                <meta
                  name="twitter:image"
                  content={
                    post.feature_image
                      ? post.feature_image.replace(
                          /ghost\.neetsupport\.com/g,
                          "https://neetsupport.com"
                        )
                      : "https://neetsupport.com/default-image.jpg"
                  }
                />
                <meta property="og:type" content="article" />

                <link
                  rel="canonical"
                  href={post.canonical_url || `${post.url}`}
                />
                <style>{FAQ_TOGGLE_STYLES}</style>
                {post.codeinjection_head && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: post.codeinjection_head,
                    }}
                  />
                )}
              </Head>
              <PostHeader
                title={post.title}
                coverImage={post.feature_image.replace("ghost.", "")}
                date={post.published_at}
                author={post.primary_author}
                readingTime={post.readingTime}
                url={post.canonical_url}
              />
              <PostBody content={post.html} />
              <ReviewSection
                userReviews={userReviews}
                setUserReviews={setUserReviews}
                userRating={userRating}
                setUserRating={setUserRating}
                reviewContent={reviewContent}
                setReviewContent={setReviewContent}
                hasVoted={hasVoted}
                setHasVoted={setHasVoted}
                API_URL={API_URL}
                postId={post.id}
                title={post.title}
                isLoggedIn={isLoggedIn}
                userId={userId}
                userName={userName}
              />
            </article>
            <div onClick={handleCardClick}>
              <Tabs />
            </div>
          </>
        )}
        <CounsellingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          college={null}
          counselling={null}
          course={[]}
          location=""
          blogTitle={post?.title || "Untitled Blog"}
        />
      </Container>
    </Layout>
  );
}

export async function getStaticProps({ params, preview = false }) {
  const { post, morePosts } = await getPostAndMorePosts(params.slug, preview);
  if (!post) {
    return {
      notFound: true,
    };
  }

  if (!post.primary_author) {
    post.primary_author = post.authors?.[0]?.name || "Unknown Author";
  }

  const stats = readingTime(post.html || post.content || "");
  post.readingTime = stats.text;

  return {
    props: {
      preview,
      post,
      morePosts: morePosts || [],
    },
    revalidate: 1,
  };
}

export async function getStaticPaths() {
  const allPosts = await getAllPostsWithSlug();
  return {
    paths: allPosts?.map(({ slug }) => `/blogs/${slug}`) || [],
    fallback: true,
  };
}
